import { gql, useQuery } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import { MenuItem, TextField } from '@mui/material';
import { UserContext } from '@providers/userContextProvider';
import { Typography } from 'hephaestus/web';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../hooks/useLocalization';
import { usePermission } from '../../../hooks/usePermission';

export const FETCH_PRODUCTS = gql`
  query fetchModelPortfolios($input: FetchModelPortfoliosInput!) {
    fetchModelPortfolios(input: $input) {
      modelPortfolios {
        id
        name
        translatedName { ${TRANSLATED_STRING_FIELDS} }
        state
      }
    }
  }
`;

const ModelPortfoliosSelect = ({
  onChange,
  value,
  label,
  disabled = false,
  readonly = false,
  themeId,
  size,
  focused,
  inheritedText,
  includeAnyOption,
  suggestableOnly,
}: {
  onChange: (event: any) => void;
  value: string;
  label: string;
  disabled?: boolean;
  readonly?: boolean;
  themeId?: string;
  inheritedText?: string;
  includeAnyOption?: boolean;
  suggestableOnly?: boolean;
  size?: 'small' | 'medium' | undefined;
  focused?: boolean;
}) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation(['components']);
  const { activeOrganization } = useContext(UserContext);
  const doReadAllThemes = usePermission('read:all_themes');

  const { loading, error, data } = useQuery(FETCH_PRODUCTS, {
    skip: !themeId,
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization.id,
          themeId,
          suggestable: doReadAllThemes ? undefined : suggestableOnly,
        },
        pagination: { perPage: 100 },
      },
    },
  });

  if (error) <Typography variant='bodyLarge'>Error</Typography>;

  return (
    <TextField
      select
      focused={focused}
      value={data ? value : ''}
      label={label}
      size={size}
      fullWidth
      onChange={onChange}
      disabled={disabled}
      InputProps={{
        readOnly: readonly,
      }}
    >
      {includeAnyOption && (
        <MenuItem value='ANY'>{t('components:any')}</MenuItem>
      )}
      {loading || !data ? (
        <MenuItem disabled>...</MenuItem>
      ) : (
        data.fetchModelPortfolios.modelPortfolios
          .filter((x: any) => x.state !== 'INACTIVE')
          .map((x: any) => (
            <MenuItem
              key={x.id}
              value={x.id}
            >{`${translateBackendString(x.translatedName) || x.name}${inheritedText ?? ''}`}</MenuItem>
          ))
      )}
    </TextField>
  );
};

export default ModelPortfoliosSelect;
